import type { LineageEdge, LineageNode } from '@/domain/fishGroups';

/**
 * Compute (x, y) positions for lineage nodes based on their signed depth
 * (ancestors negative, root 0, descendants positive). Nodes at the same
 * depth are stacked vertically and centered around y = 0.
 *
 * No external layout library required — for the typical lineage shapes
 * (linear chains, single split, single merge, split-and-merge) this layout
 * is readable and stable. We can swap to dagre later if redistribution
 * cases produce overlap.
 */

const X_SPACING = 260;
const Y_SPACING = 160;

export interface PositionedLineageNode {
    id: string;
    position: { x: number; y: number };
    node: LineageNode;
}

export function layoutLineageNodes(nodes: LineageNode[]): PositionedLineageNode[] {
    if (nodes.length === 0) return [];

    const byDepth = new Map<number, LineageNode[]>();
    for (const node of nodes) {
        const bucket = byDepth.get(node.depth);
        if (bucket) {
            bucket.push(node);
        } else {
            byDepth.set(node.depth, [node]);
        }
    }
    // Sort each bucket by id for deterministic placement across renders.
    for (const bucket of byDepth.values()) {
        bucket.sort((a, b) => a.id - b.id);
    }

    return nodes.map((node) => {
        const bucket = byDepth.get(node.depth) ?? [node];
        const indexAtDepth = bucket.findIndex((candidate) => candidate.id === node.id);
        const totalAtDepth = bucket.length;
        const y = (indexAtDepth - (totalAtDepth - 1) / 2) * Y_SPACING;
        const x = node.depth * X_SPACING;
        return {
            id: String(node.id),
            position: { x, y },
            node,
        };
    });
}

export function buildLineageEdges(edges: LineageEdge[]) {
    return edges.map((edge) => ({
        id: `e-${edge.sourceId}-${edge.destinationId}`,
        source: String(edge.sourceId),
        target: String(edge.destinationId),
    }));
}
